import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import type { JSX } from "react";
import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ValuationHubCoreModelImage } from "@/api/generatedApi";
import { DeleteIcon, Edit02Icon } from "@/assets";
import { PropertyInformation } from "@/types/refineRealEstate";

import { FormControlTextField } from "../formControls";

interface ImageCardProps {
  image: ValuationHubCoreModelImage;
  index: number;
  chip?: string;
  isPending?: boolean;
  isTitlePicture?: boolean;
  onDelete?: (imageId: string) => void;
  onTitleChange?: (imageId: string, newTitle: string) => void;
  dragHandleProps?: Record<string, unknown>;
  minHeight?: string;
  maxHeight?: string;
  readonly?: boolean;
}
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 1,
    bgcolor: "white",
  },
  imageWrapper: (minHeight?: string, maxHeight?: string): SxProps<Theme> => ({
    position: "relative",
    minHeight,
    maxHeight,
    aspectRatio: "4/3",
    display: "flex",
    overflow: "hidden",
    borderRadius: 1,
  }),
  image: { width: "100%", height: "100%", objectFit: "cover" as const },
  chip: (isTitlePicture?: boolean): SxProps<Theme> => ({
    position: "absolute",
    top: "20px",
    left: "12px",
    zIndex: 1,
    bgcolor: "#EBF4FF",
    px: "10px",
    fontWeight: 500,
    color: isTitlePicture ? "success.main" : "primary.main",
  }),
  dragButton: {
    position: "absolute",
    top: "20px",
    right: "12px",
    zIndex: 1,
    bgcolor: "white",
    borderRadius: "4px",
    p: "6px 2px",
    "&:hover": {
      bgcolor: "white",
    },
  },
  detailsBox: {
    bgcolor: "white",
    flexShrink: 0,
  },
  imageNameInput: {
    textAlign: "left",
    fontSize: "14px",
  },
  textFieldInput: {
    height: "36px",
    borderRadius: "8px",
    paddingRight: 1,
  },
  actionButton: {
    width: "36px",
    minWidth: "36px",
    height: "36px",
    p: 0,
  },
  icon: {
    fontSize: "16px",
  },
  editIcon: {
    fontSize: "20px",
  },
  divider: {
    height: "36px",
    mx: "4px",
  },
};

export const ImageCard = ({
  image,
  index,
  isTitlePicture,
  chip,
  isPending,
  onDelete,
  onTitleChange,
  dragHandleProps,
  minHeight,
  maxHeight,
  readonly,
}: ImageCardProps): JSX.Element => {
  const { t } = useTranslation("RealEstateRefinement");
  const inputRef = useRef<HTMLInputElement>(null);

  const { control } = useFormContext<PropertyInformation>();

  const handleDelete = (): void => {
    if (onDelete && image.id) onDelete(image.id);
  };

  const textFieldInputProps = {
    endAdornment: !readonly ? (
      <InputAdornment position="end">
        <Divider orientation="vertical" sx={styles.divider} />
        <IconButton
          edge="end"
          size="small"
          onClick={(): void => {
            inputRef.current?.focus();
          }}
        >
          <Edit02Icon color="primary" sx={styles.editIcon} />
        </IconButton>
      </InputAdornment>
    ) : undefined,
    sx: {
      ...styles.textFieldInput,
      "& input": styles.imageNameInput,
    },
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.imageWrapper(minHeight, maxHeight)}>
        {chip && (
          <Chip
            label={chip}
            size="small"
            color={isTitlePicture ? "success" : "primary"}
            variant="outlined"
            sx={styles.chip(isTitlePicture)}
          />
        )}
        {!readonly && (
          <IconButton sx={styles.dragButton} size="small" {...dragHandleProps}>
            <DragIndicatorIcon color="primary" />
          </IconButton>
        )}
        <img src={image.url ?? undefined} alt={image.title ?? undefined} style={styles.image} draggable={false} />
      </Box>
      <Box sx={styles.detailsBox}>
        <Typography sx={{ fontSize: "14px", color: (theme) => theme.palette.text.primary }}>
          {t("images.details")}
        </Typography>

        <Stack direction={"row"} alignItems={"end"} spacing={"15px"}>
          {isPending ? (
            <TextField
              inputRef={inputRef}
              value={image.title ?? ""}
              onChange={(e) => {
                if (onTitleChange && image.id) {
                  onTitleChange(image.id, e.target.value);
                }
              }}
              InputProps={textFieldInputProps}
              sx={{ flexGrow: 1, pt: 2 }}
              data-cy={`image-title-input-${index}`}
            />
          ) : (
            <FormControlTextField
              name={`propertyImages.images.${index}.title`}
              control={control}
              inputRef={inputRef}
              key={`image-title-${image.id}-${index}`}
              data-cy={`image-title-input-${index}`}
              InputProps={{ ...textFieldInputProps, readOnly: readonly }}
              sx={{ flexGrow: 1, pt: 2 }}
            />
          )}
          {!readonly && (
            <Button onClick={handleDelete} variant="outlined" sx={styles.actionButton}>
              <DeleteIcon sx={styles.icon} />
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
